import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// 类型定义
export interface SystemLoginSession {
  sessionId: string;
  timestamp: number;
}

export interface LoginResponse {
  code: number;
  msg?: string;
  data?: {
    positionMaster?: boolean;
    user?: {
      userInfo?: any;
      roles?: number[];
      permissions?: string[];
    };
    operator?: boolean;
    token: string;
    register?: boolean;
  };
  message?: string;
}

export interface ApiResponse<T = any> {
  code: number;
  data?: T;
  message?: string;
}

// 新增 UserChatSession 相关类型定义
export interface UserChatSession {
  userId: string;
  createdTime?: string;
  personalKnowledgeAssistantId?: string;
  personalAssistantName?: string;
  personalAssistantType: number;
  userSessionType: number;
  title?: string;
  id: string;
  isSessionActived: boolean;
}

export interface UserChatSessionResponse {
  userId: string;
  createdTime: string;
  personalKnowledgeAssistantId: string;
  personalAssistantName: string;
  personalAssistantType: number;
  userSessionType: number;
  title: string;
  id: string;
}

export interface SystemLoginContextType {
  // 状态
  isLoggedIn: boolean;
  isLoginNeeded: boolean;
  isLoading: boolean;
  retryLoginTimes: number;
  currentUserChatSession: UserChatSession | null;
  
  // 方法
  loginByPassword: (password: string, mobile: string, areaCode: string) => Promise<SystemLoginSession | null>;
  loginByWechat: (code: string, appId: string, isForce: boolean) => Promise<SystemLoginSession | null>;
  loginByGoogle: (credential: string) => Promise<SystemLoginSession | null>;
  extractMobileInfoAndSendOtp: (msgContent: string) => Promise<void>;
  protectGetRequest: <T = any>(path: string, headers?: Record<string, string>, params?: Record<string, any>) => Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }>;
  protectPostRequest: <T = any>(path: string, headers?: Record<string, string>, data?: Record<string, any>, json?: any) => Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }>;
  protectPutRequest: <T = any>(path: string, headers?: Record<string, string>, data?: Record<string, any>, json?: any) => Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }>;
  protectDeleteRequest: <T = any>(path: string, headers?: Record<string, string>, params?: Record<string, any>) => Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }>;
  protectPostFile: <T = any>(path: string, files: Record<string, File>, headers?: Record<string, string>) => Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }>;
  checkIsLoginNeeded: () => boolean;
  getServerSideCurrentTimeInShanghaiTimezone: () => Promise<string>;
  clearLoginSession: () => boolean;
  createFromStore: () => SystemLoginSession | null;
  getOrCreateLatestUserChatSession: () => Promise<UserChatSession | null>;
  describeImages: (forComposeVideoFileIds: string[], defaultLanguage?: string) => Promise<any>;
  generateImages: (userMessage: string, imageUrl?: string) => Promise<any>;
  uploadImages: (userMessage: string, uploadedFileId: string) => Promise<any>;
}

// 常量
const DB_KEY_SYSTEM_LOGIN_SESSION_KEY = '__AMT_System_Login_Session__';
const HTTP_HEADER_NAME_SESSION_TOKEN = 'userToken';
// const COLLECTION_NAME = 'ai_login_session'; // 预留给将来使用
const BIZ_SUCCESS_CODE = 1; // 服务器返回的成功代码是 1，不是 200
const LOGIN_REQUIRED_CODE = 2227;
const MAX_RETRY_TIMES = 3;

// 默认浏览器模拟器头部
const BROWSER_SIMULATOR_HEADERS = {
  'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
  'Accept': 'application/json, text/plain, */*',
  'Accept-Language': 'zh-CN,zh;q=0.9,en;q=0.8',
  'Content-Type': 'application/json',
};

// Host 工具函数
const HostUtils = {
  concatenateToFullAccessUrl: (path: string): string => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.aicaster.com.cn';
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  },
  
  concatenateToUploadUrl: (path: string): string => {
    const uploadUrl = process.env.REACT_APP_UPLOAD_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'https://apialb.aicaster.com.cn';
    return `${uploadUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }
};

// 日期时间工具函数
const DateTimeUtils = {
  getCurrentTimeStringInShanghaiTimezone: (): string => {
    return new Date().toLocaleString('zh-CN', { 
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
};

// JSON 处理工具函数 - 处理 long 类型字段精度问题
const JsonUtils = {
  /**
   * 处理 JSON 字符串中的 long 类型字段，将其转换为字符串以避免精度丢失
   * @param jsonText 原始 JSON 字符串
   * @returns 处理后的 JSON 字符串
   */
  processLongFields: (jsonText: string): string => {
    try {
      let processedText = jsonText;
      
      // 使用更精确的正则表达式，只匹配未被引号包围的长整数
      // 匹配模式：字段名后跟冒号和空格，然后是超过15位的数字，且数字后面不是引号
      const longNumberPattern = /("[\w_]+"\s*:\s*)(\d{16,})(?=\s*[,}\]])/g;
      processedText = processedText.replace(longNumberPattern, (match, prefix, number) => {
        try {
          // 检查数字是否超过 JavaScript Number.MAX_SAFE_INTEGER (2^53 - 1)
          if (number.length >= 16 || BigInt(number) > Number.MAX_SAFE_INTEGER) {
            return prefix + `"${number}"`;
          }
        } catch (error) {
          // 如果 BigInt 转换失败，说明数字很大，直接转为字符串
          return prefix + `"${number}"`;
        }
        return match;
      });

      return processedText;
    } catch (error) {
      console.error('处理长整数字段时发生错误:', error);
      // 如果处理失败，返回原始文本
      return jsonText;
    }
  },

  /**
   * 安全解析 JSON，处理 long 类型字段
   * @param jsonText 原始 JSON 字符串
   * @returns 解析后的 JSON 对象
   */
  safeParse: <T = any>(jsonText: string): T => {
    const processedText = JsonUtils.processLongFields(jsonText);
    try {
      return JSON.parse(processedText);
    } catch (error) {
      console.error('JSON解析失败:', error);
      console.error('原始JSON:', jsonText);
      throw error;
    }
  }
};

// 聊天信息定义
const ChatInfoDefine = {
  LOGIN_SUCCESSFULLY_REMINDER_MESSAGE: '登录成功！',
  LOGIN_FAILED_PASSWORD_ERROR: '密码错误，请重试',
  LOGIN_FAILED_MOBILE_ERROR: '请输入正确的手机号',
  LOGIN_FAILED_AREA_CODE_ERROR: '请输入正确的区号',
  LOGIN_FAILED_USER_NOT_FOUND_ERROR: '用户不存在',
  LOGIN_FAILED_MAX_RETRY_TIMES_ERROR: '登录失败次数过多，请稍后再试',
  SYSTEM_ERROR_REMINDER_MESSAGE: '系统错误，请稍后再试',
  ENTER_THE_OTP_REMINDER_MESSAGE: '验证码已发送，请查收',
  SEND_OTP_TOO_FREQUENTLY_MESSAGE: '发送验证码过于频繁，请稍后再试',
  MOBILE_PHONE_INFO_MSG_ERROR_MESSAGE: '手机号信息错误'
};

// 创建 Context
const SystemLoginContext = createContext<SystemLoginContextType | undefined>(undefined);

// 全局 Promise 缓存，防止重复的会话创建请求
let chatSessionCreationPromise: Promise<UserChatSession | null> | null = null;

// Context Provider
export const SystemLoginProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoginNeeded, setIsLoginNeeded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [retryLoginTimes, setRetryLoginTimes] = useState<number>(0);
  const [currentUserChatSession, setCurrentUserChatSession] = useState<UserChatSession | null>(null);

  // 从 localStorage 创建会话
  const createFromStore = useCallback((): SystemLoginSession | null => {
    try {
      const sessionData = localStorage.getItem(DB_KEY_SYSTEM_LOGIN_SESSION_KEY);
      if (sessionData) {
        const session: SystemLoginSession = JSON.parse(sessionData);
        // 检查会话是否过期（可选，根据需要调整过期时间）
        const now = Date.now();
        if (now - session.timestamp < 24 * 60 * 60 * 1000) { // 24小时过期
          return session;
        } else {
          localStorage.removeItem(DB_KEY_SYSTEM_LOGIN_SESSION_KEY);
        }
      }
    } catch (error) {
      console.error('读取登录会话失败:', error);
      localStorage.removeItem(DB_KEY_SYSTEM_LOGIN_SESSION_KEY);
    }
    return null;
  }, []);

  // 保存会话到 localStorage
  const saveToStore = useCallback((session: SystemLoginSession): void => {
    try {
      localStorage.setItem(DB_KEY_SYSTEM_LOGIN_SESSION_KEY, JSON.stringify(session));
      localStorage.setItem(HTTP_HEADER_NAME_SESSION_TOKEN, session.sessionId);
    } catch (error) {
      console.error('保存登录会话失败:', error);
    }
  }, []);

  // HTTP 请求通用处理函数
  const makeRequest = useCallback(async <T = any>(
    url: string,
    options: RequestInit,
    requiresAuth: boolean = true
  ): Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }> => {
    if (requiresAuth && isLoginNeeded) {
      return { response: null, isLoginNeeded: true };
    }

    if (requiresAuth) {
      const session = createFromStore();
      if (!session) {
        setIsLoginNeeded(true);
        setRetryLoginTimes(0);
        return { response: null, isLoginNeeded: true };
      }

      // 添加认证头
      options.headers = {
        ...options.headers,
        [HTTP_HEADER_NAME_SESSION_TOKEN]: session.sessionId,
      };
    }

    try {
      const response = await fetch(url, options);
      
      if (response.ok) {
        // 先获取原始文本，然后安全解析 JSON（处理 long 类型字段）
        const responseText = await response.text();
        
        // 只在开发环境输出详细日志
        if (process.env.NODE_ENV === 'development') {
          console.log(`HTTP响应原始文本 (${url}):`, responseText);
        }
        
        const jsonResponse: ApiResponse<T> = JsonUtils.safeParse<ApiResponse<T>>(responseText);
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`HTTP响应解析后 (${url}):`, jsonResponse);
        }
        
        // 检查是否需要重新登录
        if (jsonResponse.code === LOGIN_REQUIRED_CODE) {
          setIsLoginNeeded(true);
          setRetryLoginTimes(0);
          localStorage.removeItem(DB_KEY_SYSTEM_LOGIN_SESSION_KEY);
          setCurrentUserChatSession(null);
          return { response: null, isLoginNeeded: true };
        }

        return { response: jsonResponse, isLoginNeeded: false };
      }

      console.error(`HTTP请求失败: ${response.status}, URL: ${url}`);
      return { response: null, isLoginNeeded: false };
    } catch (error) {
      console.error(`HTTP请求异常: ${error}, URL: ${url}`);
      return { response: null, isLoginNeeded: false };
    }
  }, [isLoginNeeded, createFromStore]);

  // 使用会话保护的 GET 请求
  const protectGetRequest = useCallback(async <T = any>(
    path: string,
    headers?: Record<string, string>,
    params?: Record<string, any>
  ): Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }> => {
    let url = HostUtils.concatenateToFullAccessUrl(path);
    
    if (params) {
      const queryString = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        queryString.append(key, String(value));
      });
      url += `?${queryString.toString()}`;
    }

    const options: RequestInit = {
      method: 'GET',
      headers: {
        ...BROWSER_SIMULATOR_HEADERS,
        ...headers,
      },
    };

    return makeRequest<T>(url, options);
  }, [makeRequest]);

  // 使用会话保护的 POST 请求
  const protectPostRequest = useCallback(async <T = any>(
    path: string,
    headers?: Record<string, string>,
    data?: Record<string, any>,
    json?: any
  ): Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }> => {
    const url = HostUtils.concatenateToFullAccessUrl(path);

    let body: string | undefined;
    let contentType = BROWSER_SIMULATOR_HEADERS['Content-Type'];

    if (json) {
      body = JSON.stringify(json);
      contentType = 'application/json';
    } else if (data) {
      // 使用 URL-encoded 格式而不是 FormData，避免分段传输
      const urlEncodedData = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        urlEncodedData.append(key, String(value));
      });
      body = urlEncodedData.toString();
      contentType = 'application/x-www-form-urlencoded';
    }

    const options: RequestInit = {
      method: 'POST',
      headers: {
        ...BROWSER_SIMULATOR_HEADERS,
        ...headers,
        'Content-Type': contentType,
      },
      body,
    };

    return makeRequest<T>(url, options);
  }, [makeRequest]);

  // 使用会话保护的 PUT 请求
  const protectPutRequest = useCallback(async <T = any>(
    path: string,
    headers?: Record<string, string>,
    data?: Record<string, any>,
    json?: any
  ): Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }> => {
    const url = HostUtils.concatenateToFullAccessUrl(path);

    let body: string | undefined;
    let contentType = BROWSER_SIMULATOR_HEADERS['Content-Type'];

    if (json) {
      body = JSON.stringify(json);
      contentType = 'application/json';
    } else if (data) {
      // 使用 URL-encoded 格式而不是 FormData，避免分段传输
      const urlEncodedData = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        urlEncodedData.append(key, String(value));
      });
      body = urlEncodedData.toString();
      contentType = 'application/x-www-form-urlencoded';
    }

    const options: RequestInit = {
      method: 'PUT',
      headers: {
        ...BROWSER_SIMULATOR_HEADERS,
        ...headers,
        'Content-Type': contentType,
      },
      body,
    };

    return makeRequest<T>(url, options);
  }, [makeRequest]);

  // 使用会话保护的 DELETE 请求
  const protectDeleteRequest = useCallback(async <T = any>(
    path: string,
    headers?: Record<string, string>,
    params?: Record<string, any>
  ): Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }> => {
    let url = HostUtils.concatenateToFullAccessUrl(path);
    
    if (params) {
      const queryString = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        queryString.append(key, String(value));
      });
      url += `?${queryString.toString()}`;
    }

    const options: RequestInit = {
      method: 'DELETE',
      headers: {
        ...BROWSER_SIMULATOR_HEADERS,
        ...headers,
      },
    };

    return makeRequest<T>(url, options);
  }, [makeRequest]);

  // 使用会话保护的文件上传请求
  const protectPostFile = useCallback(async <T = any>(
    path: string,
    files: Record<string, File>,
    headers?: Record<string, string>
  ): Promise<{ response: ApiResponse<T> | null; isLoginNeeded: boolean }> => {
    if (!files || Object.keys(files).length === 0) {
      throw new Error('文件列表不能为空');
    }

    const url = HostUtils.concatenateToUploadUrl(path);
    const formData = new FormData();

    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    const options: RequestInit = {
      method: 'POST',
      headers: {
        ...headers,
        // 不设置 Content-Type，让浏览器自动设置 multipart/form-data 边界
      },
      body: formData,
    };

    return makeRequest<T>(url, options);
  }, [makeRequest]);

  // 提取手机信息并发送OTP
  const extractMobileInfoAndSendOtp = async (msgContent: string): Promise<void> => {
    const url = HostUtils.concatenateToFullAccessUrl('app-api/user/sendOtpByAiQuestion');
    
    try {
      // 使用 URL-encoded 格式而不是 FormData，避免分段传输
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('questionText', msgContent);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...BROWSER_SIMULATOR_HEADERS,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        // 先获取原始文本，然后安全解析 JSON（处理 long 类型字段）
        const responseText = await response.text();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('发送OTP响应原始文本:', responseText);
        }
        
        const jsonResponse: ApiResponse = JsonUtils.safeParse<ApiResponse>(responseText);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('发送OTP响应解析后:', jsonResponse);
        }
        
        if (jsonResponse.code === BIZ_SUCCESS_CODE) {
          if (jsonResponse.data && typeof jsonResponse.data === 'object' && 'account' in jsonResponse.data) {
            setRetryLoginTimes(0);
            return;
          }
        } else if (jsonResponse.code === 2241) {
          throw new Error(ChatInfoDefine.SEND_OTP_TOO_FREQUENTLY_MESSAGE);
        } else if (jsonResponse.code === 2244) {
          throw new Error(ChatInfoDefine.MOBILE_PHONE_INFO_MSG_ERROR_MESSAGE);
        }

        console.error('发送OTP失败:', jsonResponse);
        throw new Error(jsonResponse.message || '发送OTP失败');
      } else {
        console.error(`HTTP请求失败: ${response.status}`);
        throw new Error(`HTTP请求失败: ${response.status}`);
      }
    } catch (error) {
      console.error('发送OTP异常:', error);
      throw error;
    }
  };

  // 使用密码登录
  const loginByPassword = async (
    password: string,
    mobile: string,
    areaCode: string
  ): Promise<SystemLoginSession | null> => {
    if (!password?.trim()) {
      throw new Error(ChatInfoDefine.LOGIN_FAILED_PASSWORD_ERROR);
    }

    if (!mobile?.trim()) {
      throw new Error(ChatInfoDefine.LOGIN_FAILED_MOBILE_ERROR);
    }

    if (!areaCode?.trim()) {
      throw new Error(ChatInfoDefine.LOGIN_FAILED_AREA_CODE_ERROR);
    }

    const trimmedMobile = mobile.trim();
    const trimmedAreaCode = areaCode.trim();
    const trimmedPassword = password.trim();

    if (!isLoginNeeded) {
      return createFromStore();
    }

    setIsLoading(true);

    try {
      const url = HostUtils.concatenateToFullAccessUrl('app-api/user/loginByPassword/mobile');
      
      // 使用 URL-encoded 格式而不是 FormData，避免分段传输
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('mobile', trimmedMobile);
      urlEncodedData.append('areaCode', trimmedAreaCode);
      urlEncodedData.append('password', trimmedPassword);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...BROWSER_SIMULATOR_HEADERS,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        // 先获取原始文本，然后安全解析 JSON（处理 long 类型字段）
        const responseText = await response.text();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('登录响应原始文本:', responseText);
        }
        
        const jsonResponse: LoginResponse = JsonUtils.safeParse<LoginResponse>(responseText);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('登录响应解析后:', jsonResponse);
        }

        if (jsonResponse.code === BIZ_SUCCESS_CODE) {
          if (jsonResponse.data?.token) {
            const token = jsonResponse.data.token;
            console.log('登录成功，获取到token:', token);
            
            const loginSession: SystemLoginSession = {
              sessionId: token,
              timestamp: Date.now(),
            };

            saveToStore(loginSession);
            setIsLoginNeeded(false);
            setRetryLoginTimes(0);
            setIsLoggedIn(true);

            return loginSession;
          } else {
            console.error('登录响应中缺少token:', jsonResponse);
            throw new Error('登录响应中缺少token');
          }
        } else if (jsonResponse.code === 2235) {
          const newRetry = retryLoginTimes + 1;
          setRetryLoginTimes(newRetry);
          if (newRetry <= MAX_RETRY_TIMES) {
            throw new Error(ChatInfoDefine.LOGIN_FAILED_PASSWORD_ERROR);
          } else {
            setIsLoginNeeded(true);
            throw new Error(ChatInfoDefine.LOGIN_FAILED_MAX_RETRY_TIMES_ERROR);
          }
        } else if (jsonResponse.code === 2248) {
          const newRetry = retryLoginTimes + 1;
          setRetryLoginTimes(newRetry);
          if (newRetry <= MAX_RETRY_TIMES) {
            throw new Error(ChatInfoDefine.LOGIN_FAILED_USER_NOT_FOUND_ERROR);
          } else {
            setIsLoginNeeded(true);
            throw new Error(ChatInfoDefine.LOGIN_FAILED_MAX_RETRY_TIMES_ERROR);
          }
        }

        const newRetry = retryLoginTimes + 1;
        setRetryLoginTimes(newRetry);
        if (newRetry <= MAX_RETRY_TIMES) {
          console.error('登录失败:', jsonResponse);
          throw new Error(jsonResponse.msg || jsonResponse.message || ChatInfoDefine.SYSTEM_ERROR_REMINDER_MESSAGE);
        } else {
          setIsLoginNeeded(true);
          throw new Error(ChatInfoDefine.LOGIN_FAILED_MAX_RETRY_TIMES_ERROR);
        }
      }

      const newRetry = retryLoginTimes + 1;
      setRetryLoginTimes(newRetry);
      if (newRetry <= MAX_RETRY_TIMES) {
        console.error(`HTTP请求失败: ${response.status}`);
        throw new Error(ChatInfoDefine.SYSTEM_ERROR_REMINDER_MESSAGE);
      } else {
        setIsLoginNeeded(true);
        throw new Error(ChatInfoDefine.LOGIN_FAILED_MAX_RETRY_TIMES_ERROR);
      }
    } catch (error) {
      console.error('登录异常:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 微信登录
  const loginByWechat = async (code: string, appId: string, isForce: boolean = false): Promise<SystemLoginSession | null> => {
    if (!code?.trim()) {
      throw new Error('微信授权码不能为空');
    }

    const trimmedCode = code.trim();

    if (!isLoginNeeded && !isForce) {
      return createFromStore();
    }

    setIsLoading(true);

    try {
      const url = HostUtils.concatenateToFullAccessUrl('app-api/user/loginByWechatAuth');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...BROWSER_SIMULATOR_HEADERS,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: trimmedCode, appId: appId }),
      });

      if (response.ok) {
        // 先获取原始文本，然后安全解析 JSON（处理 long 类型字段）
        const responseText = await response.text();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('微信登录响应原始文本:', responseText);
        }
        
        const jsonResponse: LoginResponse = JsonUtils.safeParse<LoginResponse>(responseText);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('微信登录响应解析后:', jsonResponse);
        }

        if (jsonResponse.code === BIZ_SUCCESS_CODE) {
          if (jsonResponse.data?.token) {
            const token = jsonResponse.data.token;
            console.log('微信登录成功，获取到token:', token);
            
            const loginSession: SystemLoginSession = {
              sessionId: token,
              timestamp: Date.now(),
            };

            saveToStore(loginSession);
            setIsLoginNeeded(false);
            setRetryLoginTimes(0);
            setIsLoggedIn(true);

            return loginSession;
          } else {
            console.error('微信登录响应中缺少token:', jsonResponse);
            throw new Error('微信登录响应中缺少token');
          }
        }

        console.error('微信登录失败:', jsonResponse);
        throw new Error(jsonResponse.msg || jsonResponse.message || '微信登录失败');
      }

      console.error(`微信登录HTTP请求失败: ${response.status}`);
      throw new Error('微信登录失败，请重试');
    } catch (error) {
      console.error('微信登录异常:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Google登录
  const loginByGoogle = async (credential: string): Promise<SystemLoginSession | null> => {
    if (!credential?.trim()) {
      throw new Error('Google授权凭证不能为空');
    }

    const trimmedCredential = credential.trim();

    if (!isLoginNeeded) {
      return createFromStore();
    }

    setIsLoading(true);

    try {
      const url = HostUtils.concatenateToFullAccessUrl('app-api/user/loginByGoogleAuth');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...BROWSER_SIMULATOR_HEADERS,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: trimmedCredential }),
      });

      if (response.ok) {
        // 先获取原始文本，然后安全解析 JSON（处理 long 类型字段）
        const responseText = await response.text();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Google登录响应原始文本:', responseText);
        }
        
        const jsonResponse: LoginResponse = JsonUtils.safeParse<LoginResponse>(responseText);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Google登录响应解析后:', jsonResponse);
        }

        if (jsonResponse.code === BIZ_SUCCESS_CODE) {
          if (jsonResponse.data?.token) {
            const token = jsonResponse.data.token;
            console.log('Google登录成功，获取到token:', token);
            
            const loginSession: SystemLoginSession = {
              sessionId: token,
              timestamp: Date.now(),
            };

            saveToStore(loginSession);
            setIsLoginNeeded(false);
            setRetryLoginTimes(0);
            setIsLoggedIn(true);

            return loginSession;
          } else {
            console.error('Google登录响应中缺少token:', jsonResponse);
            throw new Error('Google登录响应中缺少token');
          }
        }

        console.error('Google登录失败:', jsonResponse);
        throw new Error(jsonResponse.msg || jsonResponse.message || 'Google登录失败');
      }

      console.error(`Google登录HTTP请求失败: ${response.status}`);
      throw new Error('Google登录失败，请重试');
    } catch (error) {
      console.error('Google登录异常:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 检查是否需要登录
  const checkIsLoginNeeded = useCallback((): boolean => {
    return isLoginNeeded;
  }, [isLoginNeeded]);

  // 获取服务器端上海时区的当前时间
  const getServerSideCurrentTimeInShanghaiTimezone = async (): Promise<string> => {
    const { response, isLoginNeeded: needLogin } = await protectGetRequest<{ currentTime: string }>(
      '/app-api/ai/agent/getCurrentTimeInShanghaiTimezone'
    );

    if (needLogin) {
      return DateTimeUtils.getCurrentTimeStringInShanghaiTimezone();
    }

    if (response?.code === BIZ_SUCCESS_CODE && response.data?.currentTime) {
      return response.data.currentTime;
    }

    return DateTimeUtils.getCurrentTimeStringInShanghaiTimezone();
  };

  // 清除登录会话
  const clearLoginSession = useCallback((): boolean => {
    try {
      localStorage.removeItem(DB_KEY_SYSTEM_LOGIN_SESSION_KEY);
      setIsLoginNeeded(true);
      setRetryLoginTimes(0);
      setIsLoggedIn(false);
      setCurrentUserChatSession(null);
      // 清除全局 Promise 缓存
      chatSessionCreationPromise = null;
      console.log('登录会话已清除');
      return true;
    } catch (error) {
      console.error('清除登录会话失败:', error);
      return false;
    }
  }, []);

  // 获取或创建最新的用户聊天会话
  const getOrCreateLatestUserChatSession = useCallback(async (): Promise<UserChatSession | null> => {
    if (isLoginNeeded) {
      console.warn('用户未登录，无法创建聊天会话');
      return null;
    }

    // 如果已经有聊天会话，直接返回
    if (currentUserChatSession) {
      console.log('使用现有的聊天会话:', currentUserChatSession.id);
      return currentUserChatSession;
    }

    // 如果正在创建会话，返回现有的 Promise
    if (chatSessionCreationPromise) {
      console.log('聊天会话创建请求已在进行中，等待完成...');
      return chatSessionCreationPromise;
    }

    // 创建新的会话创建 Promise
    chatSessionCreationPromise = (async (): Promise<UserChatSession | null> => {
      try {
        console.log('开始创建/获取用户聊天会话...');
        const { response, isLoginNeeded: needLogin } = await protectGetRequest<UserChatSessionResponse>(
          '/app-api/ai/agent/getOrCreateLatestAdTaskUserSession'
        );

        if (needLogin) {
          setIsLoginNeeded(true);
          console.error('获取聊天会话需要重新登录');
          return null;
        }

        if (response?.code === BIZ_SUCCESS_CODE && response.data) {
          const userSession: UserChatSession = {
            userId: response.data.userId,
            createdTime: response.data.createdTime,
            personalKnowledgeAssistantId: response.data.personalKnowledgeAssistantId,
            personalAssistantName: response.data.personalAssistantName,
            personalAssistantType: response.data.personalAssistantType,
            userSessionType: response.data.userSessionType,
            title: response.data.title,
            id: response.data.id,
            isSessionActived: false
          };

          setCurrentUserChatSession(userSession);
          console.log('成功创建/获取用户聊天会话:', userSession.id);
          return userSession;
        } else {
          console.error('获取聊天会话失败:', response);
          throw new Error('获取聊天会话失败');
        }
      } catch (error) {
        console.error('获取或创建聊天会话异常:', error);
        throw error;
      } finally {
        // 清除 Promise 缓存
        chatSessionCreationPromise = null;
      }
    })();

    return chatSessionCreationPromise;
  }, [isLoginNeeded, currentUserChatSession, protectGetRequest]);

  // 调用 describeImages 接口
  const describeImages = useCallback(async (forComposeVideoFileIds: string[], defaultLanguage: string = '简体中文'): Promise<any> => {
    if (isLoginNeeded) {
      throw new Error('用户未登录，无法调用 describeImages 接口');
    }

    if (!currentUserChatSession) {
      throw new Error('用户聊天会话未创建，无法调用 describeImages 接口');
    }

    if (!forComposeVideoFileIds || forComposeVideoFileIds.length === 0) {
      throw new Error('forComposeVideoFileIds 参数不能为空');
    }

    try {
      // 在发送给服务器时，将字符串形式的long ID转换为数字数组
      // 服务器端的JSON反序列化可以正确处理字符串形式的数字
      const requestData = {
        asyncSessionId: currentUserChatSession.id,
        forComposeVideoFileIds: forComposeVideoFileIds,
        defaultLanguage: defaultLanguage
      };

      console.log('调用 describeImages 接口，参数:', requestData);
      console.log('原始文件ID（字符串）:', forComposeVideoFileIds);
      console.log('转换后文件ID（数字）:', requestData.forComposeVideoFileIds);

      const { response, isLoginNeeded: needLogin } = await protectPostRequest(
        '/app-api/ai/agent/describeImages',
        undefined, // headers
        undefined, // data (form data)
        requestData // json data
      );

      if (needLogin) {
        setIsLoginNeeded(true);
        throw new Error('调用 describeImages 接口需要重新登录');
      }

      if (response?.code === BIZ_SUCCESS_CODE) {
        console.log('describeImages 接口调用成功:', response.data);
        return response.data;
      } else {
        console.error('describeImages 接口调用失败:', response);
        throw new Error(response?.message || `调用失败，错误代码：${response?.code}`);
      }
    } catch (error) {
      console.error('调用 describeImages 接口异常:', error);
      throw error;
    }
  }, [isLoginNeeded, currentUserChatSession, protectPostRequest]);

  // 调用 generateImages 接口
  const generateImages = useCallback(async (userMessage: string, imageUrl?: string): Promise<any> => {
    if (isLoginNeeded) {
      throw new Error('用户未登录，无法调用 generateImages 接口');
    }

    if (!currentUserChatSession) {
      throw new Error('用户聊天会话未创建，无法调用 generateImages 接口');
    }

    if (!userMessage?.trim()) {
      throw new Error('userMessage 参数不能为空');
    }

    try {
      const requestData = {
        asyncSessionId: currentUserChatSession.id,
        userMessage: userMessage.trim(),
        oriImageUrl: imageUrl || ''
      };

      console.log('调用 generateImages 接口，参数:', requestData);

      const { response, isLoginNeeded: needLogin } = await protectPostRequest(
        '/app-api/ai/agent/startToyImageGeneration',
        undefined, // headers
        undefined, // data (form data)
        requestData // json data
      );

      if (needLogin) {
        setIsLoginNeeded(true);
        throw new Error('调用 generateImages 接口需要重新登录');
      }

      if (response?.code === BIZ_SUCCESS_CODE) {
        console.log('generateImages 接口调用成功:', response.data);
        return response.data;
      } else {
        console.error('generateImages 接口调用失败:', response);
        throw new Error(response?.message || `调用失败，错误代码：${response?.code}`);
      }
    } catch (error) {
      console.error('调用 generateImages 接口异常:', error);
      throw error;
    }
  }, [isLoginNeeded, currentUserChatSession, protectPostRequest]);

  // 调用 uploadImages 接口
  const uploadImages = useCallback(async (userMessage: string, uploadedFileId: string): Promise<any> => {
    if (isLoginNeeded) {
      throw new Error('用户未登录，无法调用 uploadImages 接口');
    }

    if (!currentUserChatSession) {
      throw new Error('用户聊天会话未创建，无法调用 uploadImages 接口');
    }

    if (!userMessage?.trim() || !uploadedFileId?.trim()) {
      throw new Error('userMessage 或 imageUrl 参数不能为空');
    }

    try {
      const requestData = {
        asyncSessionId: currentUserChatSession.id,
        userMessage: userMessage.trim(),
        forComposeImageFileId: uploadedFileId || ''
      };

      console.log('调用 uploadImages 接口，参数:', requestData);

      const { response, isLoginNeeded: needLogin } = await protectPostRequest(
        '/app-api/ai/agent/uploadToyDesignedImage',
        undefined, // headers
        undefined, // data (form data)
        requestData // json data
      );

      if (needLogin) {
        setIsLoginNeeded(true);
        throw new Error('调用 uploadImages 接口需要重新登录');
      }

      if (response?.code === BIZ_SUCCESS_CODE) {
        console.log('uploadImages 接口调用成功:', response.data);
        return response.data;
      } else {
        console.error('uploadImages 接口调用失败:', response);
        throw new Error(response?.message || `调用失败，错误代码：${response?.code}`);
      }
    } catch (error) {
      console.error('调用 uploadImages 接口异常:', error);
      throw error;
    }
  }, [isLoginNeeded, currentUserChatSession, protectPostRequest]);
  
  // 初始化检查登录状态
  useEffect(() => {
    const session = createFromStore();
    if (session) {
      setIsLoggedIn(true);
      setIsLoginNeeded(false);
    } else {
      setIsLoggedIn(false);
      setIsLoginNeeded(true);
    }
  }, [createFromStore]);

  const contextValue: SystemLoginContextType = {
    // 状态
    isLoggedIn,
    isLoginNeeded,
    isLoading,
    retryLoginTimes,
    currentUserChatSession,
    
    // 方法
    loginByPassword,
    loginByWechat,
    loginByGoogle,
    extractMobileInfoAndSendOtp,
    protectGetRequest,
    protectPostRequest,
    protectPutRequest,
    protectDeleteRequest,
    protectPostFile,
    checkIsLoginNeeded,
    getServerSideCurrentTimeInShanghaiTimezone,
    clearLoginSession,
    createFromStore,
    getOrCreateLatestUserChatSession,
    describeImages,
    generateImages,
    uploadImages,
  };

  return (
    <SystemLoginContext.Provider value={contextValue}>
      {children}
    </SystemLoginContext.Provider>
  );
};

// Hook for using the context
export const useSystemLogin = (): SystemLoginContextType => {
  const context = useContext(SystemLoginContext);
  if (context === undefined) {
    throw new Error('useSystemLogin must be used within a SystemLoginProvider');
  }
  return context;
};

export default SystemLoginContext;